import * as React from "react";
import { Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Shared search/status/paging behaviour for the master-data tabs. The vendor
 * catalog runs to hundreds of rows once bootstrapped from QuickBooks, so a
 * plain table is unusable without these.
 */

export type MasterStatusFilter = "active" | "inactive" | "all";

const PAGE_SIZE = 100;

export function useMasterFilter<T extends { active: boolean }>(
  rows: T[] | undefined,
  searchText: (row: T) => string,
) {
  const [search, setSearch] = React.useState("");
  const [status, setStatus] = React.useState<MasterStatusFilter>("active");
  const [limit, setLimit] = React.useState(PAGE_SIZE);

  // A new search is a new result set; go back to the first page.
  React.useEffect(() => {
    setLimit(PAGE_SIZE);
  }, [search, status]);

  const filtered = React.useMemo(() => {
    if (!rows) return undefined;
    const needle = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (status === "active" && !row.active) return false;
      if (status === "inactive" && row.active) return false;
      if (!needle) return true;
      return searchText(row).toLowerCase().includes(needle);
    });
    // searchText is a stable inline selector in every call site.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, search, status]);

  const visible = React.useMemo(
    () => filtered?.slice(0, limit),
    [filtered, limit],
  );

  return {
    search,
    setSearch,
    status,
    setStatus,
    filtered,
    visible,
    hasMore: (filtered?.length ?? 0) > (visible?.length ?? 0),
    showMore: () => setLimit((prev) => prev + PAGE_SIZE),
    showAll: () => setLimit(Number.MAX_SAFE_INTEGER),
  };
}

const STATUS_TABS: { value: MasterStatusFilter; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
  { value: "all", label: "All" },
];

export function MasterToolbar({
  search,
  onSearchChange,
  status,
  onStatusChange,
  counts,
  placeholder,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  status: MasterStatusFilter;
  onStatusChange: (value: MasterStatusFilter) => void;
  counts: { active: number; inactive: number; all: number };
  placeholder: string;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative min-w-56 flex-1">
        <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 h-3.5 w-3.5 -translate-y-1/2" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={placeholder}
          className="h-8 pl-8"
          aria-label={placeholder}
        />
      </div>
      <nav className="bg-muted text-muted-foreground flex w-fit items-center gap-1 rounded-lg p-[3px]">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.value}
            type="button"
            onClick={() => onStatusChange(tab.value)}
            aria-pressed={status === tab.value}
            className={cn(
              "rounded-md px-2.5 py-1 text-xs font-medium transition-colors",
              status === tab.value
                ? "bg-background text-foreground"
                : "hover:text-foreground",
            )}
          >
            {tab.label}
            <span className="ml-1.5 tabular-nums opacity-60">
              {counts[tab.value]}
            </span>
          </button>
        ))}
      </nav>
    </div>
  );
}

export function MasterTableFooter({
  shown,
  total,
  hasMore,
  onShowMore,
  onShowAll,
}: {
  shown: number;
  total: number;
  hasMore: boolean;
  onShowMore: () => void;
  onShowAll: () => void;
}) {
  if (!total) return null;
  return (
    <div className="flex items-center justify-between px-1">
      <p className="text-muted-foreground text-xs tabular-nums">
        Showing {shown} of {total}
      </p>
      {hasMore && (
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={onShowMore}>
            Show 100 more
          </Button>
          <Button variant="ghost" size="sm" onClick={onShowAll}>
            Show all
          </Button>
        </div>
      )}
    </div>
  );
}
